import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import RightDrawer from "../../Rightpanel/RightDrawer";
import { getAdminRequestDetail } from "../../../_services/adminRequests.service";
import styles from "./AdminRequestDrawer.module.scss";

// Champs specifiques a afficher selon le type de demande.
const TYPE_FIELDS = {
  calibration: [
    { key: "type_label", labelKey: "detail_type_label" },
    { key: "serial_dragonfly", labelKey: "detail_serial_dragonfly" },
    { key: "serial_co", labelKey: "detail_serial_co" },
  ],
  sav: [
    { key: "material_type", labelKey: "detail_material_type" },
    { key: "material_model", labelKey: "detail_material_model" },
    { key: "serial_number", labelKey: "detail_serial_number" },
    { key: "issue", labelKey: "detail_issue" },
  ],
  support: [
    { key: "category", labelKey: "detail_category" },
    { key: "priority", labelKey: "detail_priority" },
    { key: "subject", labelKey: "detail_subject" },
    { key: "description", labelKey: "detail_description" },
  ],
};

const AdminRequestDrawer = ({ open, type, requestId, onClose, onDownload }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !type || !requestId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setDetail(null);
    getAdminRequestDetail(type, requestId, controller.signal)
      .then((data) => setDetail(data))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(t("admin_requests.detail.load_error"));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, type, requestId, t]);

  // Ferme avec ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const fmtDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  };

  const Row = ({ label, value, block }) => {
    if (value === undefined || value === null || value === "") return null;
    return (
      <div className={block ? styles.rowBlock : styles.row}>
        <span className={styles.rowLabel}>{label}</span>
        <span className={styles.rowValue}>{value}</span>
      </div>
    );
  };

  const specificFields = detail && type ? TYPE_FIELDS[type] || [] : [];

  return (
    <RightDrawer
      isOpen={open}
      onClose={onClose}
      title={t("admin_requests.detail.title")}
      width="480px"
    >
      {loading ? (
        <div className={styles.info}>{t("admin_requests.loading")}</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : !detail ? (
        <div className={styles.info}>{t("admin_requests.empty")}</div>
      ) : (
        <div className={styles.content}>
          {/* En-tete */}
          <div className={styles.badgeRow}>
            <span className={styles.typeBadge}>
              {t(`admin_requests.type_${type}`)}
            </span>
            {detail.pdf_available ? (
              <span className={styles.badgeOk}>
                {t("admin_requests.status_available")}
              </span>
            ) : (
              <span className={styles.badgeArchived}>
                {t("admin_requests.status_archived")}
              </span>
            )}
          </div>

          <h3 className={styles.ref}>{detail.ref_number || "-"}</h3>

          {/* Section : informations generales */}
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              {t("admin_requests.detail.section_general")}
            </h4>
            <Row label={t("admin_requests.detail.request_date")} value={detail.request_date} />
            <Row label={t("admin_requests.detail.created_at")} value={fmtDate(detail.created_at)} />
          </section>

          {/* Section : demandeur */}
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              {t("admin_requests.detail.section_requester")}
            </h4>
            <Row label={t("admin_requests.detail.name")} value={detail.name} />
            <Row label={t("admin_requests.detail.email")} value={detail.email} />
            <Row label={t("admin_requests.detail.phone")} value={detail.phone} />
            <Row label={t("admin_requests.detail.cabine")} value={detail.cabine_name} />
          </section>

          {/* Section : adresse */}
          {(detail.address || detail.zip_code || detail.city || detail.country) && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("admin_requests.detail.section_address")}
              </h4>
              <Row label={t("admin_requests.detail.address")} value={detail.address} />
              <Row label={t("admin_requests.detail.zip_code")} value={detail.zip_code} />
              <Row label={t("admin_requests.detail.city")} value={detail.city} />
              <Row label={t("admin_requests.detail.country")} value={detail.country} />
            </section>
          )}

          {/* Section : details specifiques au type */}
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              {t("admin_requests.detail.section_specific")}
            </h4>
            {specificFields.map((f) => (
              <Row
                key={f.key}
                label={t(`admin_requests.detail.${f.labelKey}`)}
                value={detail[f.key]}
                block={["issue", "description"].includes(f.key)}
              />
            ))}
          </section>

          {/* Section : message */}
          {detail.message && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("admin_requests.detail.message")}
              </h4>
              <p className={styles.message}>{detail.message}</p>
            </section>
          )}

          {/* Section : PDF */}
          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>
              {t("admin_requests.detail.section_pdf")}
            </h4>
            <Row label={t("admin_requests.detail.pdf_filename")} value={detail.pdf_filename} />
            <Row label={t("admin_requests.detail.pdf_expires_at")} value={fmtDate(detail.pdf_expires_at)} />
            {detail.pdf_deleted_at && (
              <Row
                label={t("admin_requests.detail.pdf_deleted_at")}
                value={fmtDate(detail.pdf_deleted_at)}
              />
            )}
          </section>

          {/* Action : telechargement */}
          {detail.pdf_available && (
            <button
              type="button"
              className={styles.downloadBtn}
              onClick={() => onDownload(detail)}
            >
              <FiDownload />
              {t("admin_requests.download")}
            </button>
          )}
        </div>
      )}
    </RightDrawer>
  );
};

export default AdminRequestDrawer;
